import * as localForage from 'localforage';
import { databaseVersionReady } from './indexeddb-version';
import {
  canRuntimePersist,
  getRuntimeCacheEpoch,
  initializeRuntimeCacheEpoch,
  LEGACY_CACHE_EPOCH,
} from './session-cache';

type StorageSnapshot = { [key: string]: any };

let snapshot: Promise<StorageSnapshot> | null = null;
// Lo stesso oggetto risolto da `snapshot`, disponibile in modo sincrono una volta letto IDB.
// Resta null finche' la lettura non e' completata o se e' fallita (vedi getItem).
let loadedSnapshot: StorageSnapshot | null = null;
let writesSuspended = false;
const pendingWrites = new Set<Promise<any>>();

/**
 * Quante entita' conteneva IDB per ogni store al momento dell'avvio. E' una fotografia: non viene
 * aggiornata dalle scritture successive, e serve solo a rispondere alla domanda "questo store
 * doveva ripopolarsi?" - la stessa a cui prima rispondeva il flag hasValue su localStorage, che
 * poteva divergere dal contenuto reale di IDB.
 */
const persistedEntityCounts: { [storeName: string]: number } = {};

const recordEntityCounts = (record: any) => {
  if (!record || typeof record !== 'object') {
    return;
  }
  // Un record di persistState e' { [nomeStore]: stato, $cache?: ... }.
  for (const [storeName, state] of Object.entries<any>(record)) {
    if (storeName === '$cache') {
      continue;
    }
    if (state && Array.isArray(state.ids)) {
      persistedEntityCounts[storeName] = state.ids.length;
    }
  }
};

/**
 * Legge tutto il contenuto di localForage in una sola passata: un cursore, una transazione IDB.
 * Sostituisce le ~95 getItem indipendenti che partivano insieme all'avvio, ognuna con la sua
 * transazione, e la cui coda era la causa per cui l'idratazione degli store poteva sforare la
 * finestra di replay di Akita ($$addStore e' un ReplaySubject(50, 5000)).
 *
 * Il picco di memoria non peggiora: anche prima i valori di tutte le chiavi erano in RAM insieme,
 * uno per ogni getItem risolta in attesa di essere consumata dal proprio store. Qui in piu' ogni
 * voce viene liberata appena servita.
 *
 * Lazy: la passata parte alla prima getItem, cioe' quando localForage.config() e' gia' stato
 * applicato da localforage.settings.ts.
 */
const physicalKey = (storeKey: string, epoch: string): string =>
  epoch === LEGACY_CACHE_EPOCH ? storeKey : `${epoch}::${storeKey}`;

const logicalKey = (persistedKey: string, epoch: string): string | null => {
  if (epoch === LEGACY_CACHE_EPOCH) {
    return persistedKey.includes('::') ? null : persistedKey;
  }
  const prefix = `${epoch}::`;
  return persistedKey.startsWith(prefix) ? persistedKey.slice(prefix.length) : null;
};

const track = <T>(operation: Promise<T>): Promise<T> => {
  pendingWrites.add(operation);
  return operation.finally(() => pendingWrites.delete(operation));
};

/**
 * Elimina gli snapshot delle epoch precedenti scorrendo esclusivamente le chiavi. I valori degli
 * store (che possono contenere migliaia di entita') non vengono trasferiti ne' deserializzati.
 */
const purgeObsoleteEpochs = async (activeEpoch: string): Promise<void> => {
  await databaseVersionReady;
  await localForage.ready();
  const config = localForage.config();

  await new Promise<void>((resolve, reject) => {
    // La versione esposta da config() e' quella richiesta inizialmente, non necessariamente la
    // versione reale a cui LocalForage si e' riallineato. Ometterla apre la versione corrente ed
    // evita un VersionError quando il DB e' gia' stato portato a una versione superiore.
    const request = indexedDB.open(config.name);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => {
      const db = request.result;
      let transaction: IDBTransaction;
      try {
        transaction = db.transaction(config.storeName, 'readwrite');
      } catch (error) {
        db.close();
        reject(error);
        return;
      }
      const store = transaction.objectStore(config.storeName);
      const cursorRequest = store.openKeyCursor();

      cursorRequest.onerror = () => reject(cursorRequest.error);
      cursorRequest.onsuccess = () => {
        const cursor = cursorRequest.result;
        if (!cursor) {
          return;
        }
        if (logicalKey(String(cursor.key), activeEpoch) === null) {
          // openKeyCursor non materializza il valore, ma proprio per questo non consente
          // cursor.delete(). La cancellazione tramite object store resta key-only.
          store.delete(cursor.primaryKey);
        }
        cursor.continue();
      };
      transaction.oncomplete = () => {
        db.close();
        resolve();
      };
      transaction.onerror = () => {
        db.close();
        reject(transaction.error);
      };
      transaction.onabort = () => {
        db.close();
        reject(transaction.error);
      };
    };
  });
};

const loadSnapshot = (): Promise<StorageSnapshot> => {
  if (snapshot) {
    return snapshot;
  }
  const started = performance.now();
  snapshot = databaseVersionReady
    .then(() => initializeRuntimeCacheEpoch())
    .then(async (epoch) => {
      await purgeObsoleteEpochs(epoch);
      const acc: StorageSnapshot = {};
      await localForage.iterate<any, void>((value, key) => {
        const storeKey = logicalKey(key, epoch);
        if (storeKey === null) {
          return;
        }
        acc[storeKey] = value;
        recordEntityCounts(value);
      });
      console.log(`[AKITA STORAGE] snapshot IDB epoch ${epoch}: ${Object.keys(acc).length} chiavi in ${Math.round(performance.now() - started)}ms`);
      loadedSnapshot = acc;
      return acc;
    })
    .catch((error) => {
      // IDB illeggibile (quota, DB corrotto, upgrade bloccato da un'altra tab): si prosegue con
      // snapshot vuoto. Ogni getItem ricade sulla lettura puntuale e, se anche quella fallisce,
      // gli store partono vuoti e vengono ripopolati dal realtime. loadedSnapshot resta null:
      // senza una fotografia completa non possiamo rispondere in modo sincrono.
      console.error('[AKITA STORAGE] lettura snapshot IDB fallita', error);
      return {};
    });
  return snapshot;
};

const readFromSnapshot = (snap: StorageSnapshot, key: string): { found: boolean; value?: any } => {
  if (!(key in snap)) {
    return { found: false };
  }
  const value = snap[key];
  // Servita una volta sola: da qui in avanti la sorgente autoritativa e' IDB e la RAM si libera.
  delete snap[key];
  return { found: true, value };
};

const getItemAsync = async (key: string): Promise<any> => {
  const snap = await loadSnapshot();
  const hit = readFromSnapshot(snap, key);
  if (hit.found) {
    return hit.value;
  }
  const epoch = await initializeRuntimeCacheEpoch();
  return localForage.getItem(physicalKey(key, epoch));
};

/**
 * Storage per persistState: le letture arrivano dallo snapshot unico, le scritture vanno su
 * localForage chiave per chiave. Le epoch successive alla migrazione usano un prefisso fisico;
 * Akita continua invece a vedere le chiavi logiche originali.
 */
export const akitaPersistStorage = {
  /**
   * Finche' lo snapshot non e' in memoria (persistState statiche, create al load del modulo) la
   * lettura e' asincrona. Dopo, risponde in modo SINCRONO: lo snapshot e' la fotografia completa di
   * IDB per l'epoch corrente, quindi una chiave assente non ha stato persistito (o e' gia' stata
   * servita) e vale null senza interrogare IDB.
   *
   * La risposta sincrona e' cio' che rende deterministica la persistenza degli store creati a runtime
   * (meta interfacce): persistState di Akita sottoscrive $$addStore solo dopo che getItem ha risposto,
   * e $$addStore e' un ReplaySubject(50, 5000). Con una promise la sottoscrizione arriva dopo la
   * creazione dello store e dipende dal replay: una lettura IDB in coda dietro le scritture del
   * first-sync (>5s) o piu' di 50 store creati in un colpo la fanno saltare, e lo store non viene mai
   * ne' idratato ne' persistito. Con un valore sincrono persistState sottoscrive dentro la propria
   * chiamata: creando lo store DOPO (vedi MetaInterfaceServiceProvider.loadStores) l'evento arriva
   * live, senza replay.
   *
   * Unico caso non coperto: una chiave scritta in IDB da un'altra tab dopo il nostro avvio. Lo store
   * parte vuoto e viene ripopolato dal realtime, che e' anche la sorgente dell'altra tab.
   */
  getItem(key: string): any | Promise<any> {
    if (loadedSnapshot) {
      const hit = readFromSnapshot(loadedSnapshot, key);
      return hit.found ? hit.value : null;
    }
    return getItemAsync(key);
  },

  setItem(key: string, value: any): Promise<any> {
    if (writesSuspended || !canRuntimePersist()) {
      return Promise.resolve(value);
    }
    return track(localForage.setItem(physicalKey(key, getRuntimeCacheEpoch()), value));
  },

  removeItem(key: string): Promise<void> {
    if (writesSuspended || !canRuntimePersist()) {
      return Promise.resolve();
    }
    return track(localForage.removeItem(physicalKey(key, getRuntimeCacheEpoch())));
  },

  clear(): Promise<void> {
    loadedSnapshot = {};
    snapshot = Promise.resolve(loadedSnapshot);
    Object.keys(persistedEntityCounts).forEach((storeName) => delete persistedEntityCounts[storeName]);
    return localForage.clear();
  },

  /**
   * Barriera del cambio sessione. Il marker globale blocca le altre tab; questa sospensione copre
   * anche i debounce locali non ancora arrivati a setItem. La transazione key-only parte soltanto
   * dopo le scritture gia' iniziate in questa tab e, essendo readwrite sullo stesso object store,
   * viene serializzata da IndexedDB anche rispetto alle transazioni gia' partite nelle altre tab.
   */
  async prepareForNextEpoch(nextEpoch: string): Promise<void> {
    writesSuspended = true;
    await Promise.all(Array.from(pendingWrites).map((operation) => operation.catch(() => undefined)));
    await purgeObsoleteEpochs(nextEpoch);
    loadedSnapshot = {};
    snapshot = Promise.resolve(loadedSnapshot);
    Object.keys(persistedEntityCounts).forEach((storeName) => delete persistedEntityCounts[storeName]);
  }
};

/**
 * Numero di entita' che IDB conteneva per quello store all'avvio. 0 se la chiave non c'era, se era
 * vuota o se non e' un entity store: in tutti quei casi non c'e' nulla da ripopolare.
 */
export const persistedEntityCount = (storeName: string): number => persistedEntityCounts[storeName] || 0;

/** Si risolve quando lo snapshot e' stato letto (o quando la lettura e' fallita). */
export const whenPersistedSnapshotReady = (): Promise<void> => loadSnapshot().then(() => undefined);

/** true quando getItem risponde in modo sincrono (snapshot IDB in memoria, lettura riuscita). */
export const isPersistedSnapshotLoaded = (): boolean => loadedSnapshot !== null;
